import { NoGrafoResponse } from '@interfaces/corporate-registry/responses/rede-societaria.response';

/**
 * Geometria do mapa societário, espelhando o `GrafoRede` do Vault-Dossiê (react-force-graph-2d):
 * nós com raio FIXO na tela (12/9/8 px, independente do zoom), `zoomToFit` com 70px de margem e
 * zoom em torno do cursor. Tudo aqui é puro para poder ser testado.
 */
export const CORES_GRAFO = {
  raiz: '#00e220',
  empresa: '#093489',
  pessoa: '#21b5ea',
  alerta: '#d92d20',
  aresta: '#c9d6ee',
} as const;

/** Transformação grafo → tela: `sx = x * k + tx`, `sy = y * k + ty`. */
export interface Transformacao {
  k: number;
  tx: number;
  ty: number;
}

/** Margem do `zoomToFit(300, 70)` do Dossiê. */
export const MARGEM_AJUSTE = 70;
const ZOOM_MIN = 0.02;
const ZOOM_MAX = 40;
/** Teto do ajuste: grafo de um ponto só não pode virar um zoom infinito. */
const K_AJUSTE_MAX = 2.5;

const coord = (v: number | null | undefined): number => (Number.isFinite(v) ? (v as number) : 0);

/**
 * O `zoomToFit` do Dossiê: enquadra todos os nós na área útil (largura/altura menos a margem),
 * centralizado. Nó sem coordenada conta como (0,0). Nunca devolve NaN nem k ≤ 0.
 */
export function ajustarAoQuadro(
  nos: Pick<NoGrafoResponse, 'x' | 'y'>[],
  largura: number,
  altura: number,
  margem = MARGEM_AJUSTE
): Transformacao {
  const w = Math.max(1, largura);
  const h = Math.max(1, altura);
  if (!nos.length) return { k: 1, tx: w / 2, ty: h / 2 };

  const xs = nos.map(n => coord(n.x));
  const ys = nos.map(n => coord(n.y));
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const uteisX = Math.max(1, w - margem * 2);
  const uteisY = Math.max(1, h - margem * 2);
  const kx = maxX > minX ? uteisX / (maxX - minX) : K_AJUSTE_MAX;
  const ky = maxY > minY ? uteisY / (maxY - minY) : K_AJUSTE_MAX;
  const k = Math.min(K_AJUSTE_MAX, kx, ky);

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  return { k, tx: w / 2 - cx * k, ty: h / 2 - cy * k };
}

/** Zoom em torno do ponto de tela (px, py): o ponto sob o cursor fica parado. */
export function zoomEm(t: Transformacao, fator: number, px: number, py: number): Transformacao {
  const k = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, t.k * fator));
  const real = k / t.k;
  return { k, tx: px - (px - t.tx) * real, ty: py - (py - t.ty) * real };
}

/** `destacarRaiz=false` pinta a empresa consultada como as demais (opção `highlightRoot`). */
export function corDoNo(no: NoGrafoResponse, destacarRaiz = true): string {
  if (no.raiz && destacarRaiz) return CORES_GRAFO.raiz;
  return no.tipo === 'pessoa' ? CORES_GRAFO.pessoa : CORES_GRAFO.empresa;
}

/** Raio em px de TELA, como no Dossiê (`raio / scale`). */
export function raioDoNo(no: NoGrafoResponse, destacarRaiz = true): number {
  if (no.raiz && destacarRaiz) return 12;
  return no.tipo === 'empresa' ? 9 : 8;
}

/** Empresa fora da situação 02 (Ativa) ganha anel vermelho. Pessoa não tem situação. */
export function emAlerta(no: NoGrafoResponse): boolean {
  return no.tipo === 'empresa' && !!no.situacaoCodigo && no.situacaoCodigo !== '02';
}

/**
 * Seta da aresta, como `linkDirectionalArrowLength=3.5` + `RelPos=0.92` do Dossiê: triângulo a
 * 92% do caminho, com comprimento 3.5 em unidades do grafo (escala com o zoom). Coordenadas de tela.
 */
export function pontaDaSeta(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  k: number
): string | null {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const d = Math.hypot(dx, dy);
  if (d < 1) return null;
  const len = 3.5 * k;
  const ux = dx / d;
  const uy = dy / d;
  const px = x1 + dx * 0.92;
  const py = y1 + dy * 0.92;
  const bx = px - ux * len;
  const by = py - uy * len;
  const meia = len * 0.5;
  return `${px},${py} ${bx - uy * meia},${by + ux * meia} ${bx + uy * meia},${by - ux * meia}`;
}
